import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import type { AssetFilters } from '../../api/asset.api';
import type { Audit } from '../../api/models/audit.model.ts';

interface AssetsState {
	search: string;
	filters: AssetFilters;
	selectedAssetSerialNo?: string | null;
	audit: AuditState;
}

interface AuditState {
	auditLogs: Audit[];
	loading: boolean;
	error: string | null;
}

const initialAuditState: AuditState = {
	auditLogs: [],
	loading: false,
	error: null,
};

const initialState: AssetsState = {
	search: '',
	filters: { userId: '' },
	selectedAssetSerialNo: null,
	audit: initialAuditState,
};

const assetsSlice = createSlice({
	name: 'assets',
	initialState: initialState,
	reducers: {
		setSearch(state, action: PayloadAction<string>) {
			state.search = action.payload;
		},
		setFilters(state, action: PayloadAction<AssetFilters>) {
			state.filters = action.payload;
		},
		clearFilters(state) {
			state.filters = { userId: '' };
		},
		setSelectedAssetSerialNo(state, action: PayloadAction<string | null>) {
			state.selectedAssetSerialNo = action.payload;
		},
		clearSelectedAssetSerialNo(state) {
			state.selectedAssetSerialNo = null;
		},
		setAuditLogs(state, action: PayloadAction<Audit[]>) {
			state.audit.auditLogs = action.payload;
			state.audit.loading = false;
			state.audit.error = null;
		},
		setAuditLoading(state, action: PayloadAction<boolean>) {
			state.audit.loading = action.payload;
		},
		setAuditError(state, action: PayloadAction<string | null>) {
			state.audit.error = action.payload;
			state.audit.loading = false;
		},
		clearAuditLogs(state) {
			state.audit.auditLogs = [];
			state.audit.error = null;
			state.audit.loading = false;
		},
	},
});

export const {
	setSearch,
	setFilters,
	clearFilters,
	setSelectedAssetSerialNo,
	clearSelectedAssetSerialNo,
	setAuditLogs,
	setAuditLoading,
	setAuditError,
	clearAuditLogs,
} = assetsSlice.actions;
export const selectSelectedAssetSerialNo = (state: RootState) => state.assets.selectedAssetSerialNo;
export const selectAssetsSearch = (state: RootState) => state.assets.search;
export const selectAssetsFilters = (state: RootState) => state.assets.filters;

// Use assetsApi hooks (useGetAssetsQuery) in components for fetching assets.

export default assetsSlice.reducer;
